import type { AgentSession } from 'api/agentSessions/agentSessions.api';
import {
  acceptSessionProjectSuggestions,
  generateProjectSuggestion,
  getSessionProjectSuggestions,
  PredictionUnfinished,
  type ProjectSuggestion,
  type UnfinishedReason,
  undoSessionProjectSuggestions,
} from 'api/agentSessions/projectSuggestions.api';
import { uniq } from 'lodash-es';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { AgentPeriod } from './agentTracking.model';
import { assignErrorMessage } from './projectAssignmentError';
import { hasDisplayableProject } from './projectSuggestionChoices';

/** How long a requested check has been running, in steps a person can read. */
export type CheckProgress = 'finding' | 'still' | 'slow';
// A healthy check takes about two seconds; past these points the picker says so.
export const STILL_CHECKING_AFTER_MS = 5000;
export const SLOW_CHECK_AFTER_MS = 15000;
/** How long the project button stays highlighted after a suggestion arrives. */
export const ARRIVED_HIGHLIGHT_MS = 4000;
// Asking again cannot change these outcomes, so they get no "Try again".
const FINAL_REASONS: UnfinishedReason[] = ['no_projects', 'limit_reached', 'replaced'];

export type ReviewState = {
  suggestions: Record<number, ProjectSuggestion>;
  busy: string | null;
  error: string | null;
  /** Visible while work is held up for a reason the person should know, e.g. offline. */
  notice?: string | null;
  /** Set while a requested check runs; advances as it takes longer than usual. */
  progress?: CheckProgress | null;
  /** Repeats the checks that did not finish, when asking again can help. */
  retry?: (() => void) | null;
  /** Briefly true after requested checks produced a suggestion, to draw the eye to the row. */
  arrived?: boolean;
  generate: (ids?: number[]) => Promise<void>;
  /** `fillIds`: unassigned periods without their own suggestion that take the accepted project. */
  accept: (ids: number[], projectId?: number, fillIds?: number[]) => Promise<boolean>;
  undo: () => Promise<void>;
  canUndo: boolean;
};
const defaultFetchers = {
  getSessionProjectSuggestions,
  generateProjectSuggestion,
  acceptSessionProjectSuggestions,
  undoSessionProjectSuggestions,
};
const OFFLINE_NOTICE = 'You’re offline. We’ll keep checking once your connection is back.';
const Context = createContext<ReviewState | null>(null);
export const useSessionProjectSuggestions = () => useContext(Context);

// Keep receipts above project/day groups: accepting can move the row and
// unmount its provider. Receipts stay local to the table's lifetime.
const ReceiptContext = createContext<{
  tokens: Record<number, string>;
  setToken: (sessionId: number, token: string | null) => void;
} | null>(null);

export function ProjectSuggestionReceipts({ children }: { children: ReactNode }) {
  const [tokens, setTokens] = useState<Record<number, string>>({});
  const setToken = useCallback((sessionId: number, token: string | null) => {
    setTokens(previous => {
      const next = { ...previous };
      if (token) next[sessionId] = token;
      else delete next[sessionId];
      return next;
    });
  }, []);
  // Stable unless a receipt changes: every session row reads this, and a new value on each table
  // render would re-render all of them (and their project cells) on every live refresh.
  const value = useMemo(() => ({ tokens, setToken }), [tokens, setToken]);
  return <ReceiptContext.Provider value={value}>{children}</ReceiptContext.Provider>;
}

export function SessionProjectSuggestions({
  periods,
  enabled,
  changeSession,
  onAccepted,
  children,
  fetchers = defaultFetchers,
}: {
  periods: AgentPeriod[];
  enabled: boolean;
  /** Sends the request in turn with the page's other session changes, then applies its session. */
  changeSession?: (request: () => Promise<AgentSession>) => Promise<AgentSession>;
  /** Projects the user just accepted, in acceptance order, so they can count as recently used. */
  onAccepted?: (projectIds: number[]) => void;
  children: ReactNode;
  fetchers?: typeof defaultFetchers;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [progress, setProgress] = useState<CheckProgress | null>(null);
  const [retryIds, setRetryIds] = useState<number[] | null>(null);
  const [arrived, setArrived] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const clearTimers = () => {
    for (const timer of timers.current) clearTimeout(timer);
    timers.current = [];
  };
  useEffect(
    () => () => {
      for (const timer of timers.current) clearTimeout(timer);
    },
    []
  );
  const clearError = () => {
    setError(null);
    setRetryIds(null);
  };
  const receipts = useContext(ReceiptContext);
  const [localUndoToken, setLocalUndoToken] = useState<string | null>(null);
  const generation = useRef(0);
  const sessionId = periods[0].sessionId;
  const undoToken = receipts ? receipts.tokens[sessionId] : localUndoToken;
  const setUndoToken = (token: string | null) => {
    if (receipts) receipts.setToken(sessionId, token);
    else setLocalUndoToken(token);
  };
  const scopeKey = JSON.stringify(
    periods.map(p => [
      p.segmentId,
      p.projectId,
      p.summary,
      p.projectPredictionState,
      p.projectSuggestion === undefined ? 'omitted' : (p.projectSuggestion?.fingerprint ?? null),
      p.projectSuggestion?.generated_at,
    ])
  );

  const hasInlineSuggestions = periods.every(p => p.projectSuggestion !== undefined);
  const inlineSnapshot = useRef<{ key: string; suggestions: Record<number, ProjectSuggestion> }>();
  if (inlineSnapshot.current?.key !== scopeKey) {
    inlineSnapshot.current = {
      key: scopeKey,
      suggestions: Object.fromEntries(
        periods.flatMap(p => (p.projectSuggestion ? [[p.segmentId, p.projectSuggestion]] : []))
      ),
    };
  }
  const initialSuggestions = inlineSnapshot.current.suggestions;
  const [snapshot, setSnapshot] = useState({ scopeKey, suggestions: initialSuggestions });
  // A new row snapshot must be visible on its first render, before effects run.
  const suggestions = snapshot.scopeKey === scopeKey ? snapshot.suggestions : initialSuggestions;
  const setSuggestions = (
    update:
      | Record<number, ProjectSuggestion>
      | ((previous: Record<number, ProjectSuggestion>) => Record<number, ProjectSuggestion>)
  ) => {
    setSnapshot(previous => ({
      scopeKey,
      suggestions:
        typeof update === 'function'
          ? update(previous.scopeKey === scopeKey ? previous.suggestions : initialSuggestions)
          : update,
    }));
  };

  useEffect(() => {
    const version = ++generation.current;
    setSnapshot({ scopeKey, suggestions: initialSuggestions });
    setError(null);
    setRetryIds(null);
    setBusy(null);
    setNotice(null);
    setProgress(null);
    const scope = JSON.parse(scopeKey) as [number, ...unknown[]][];
    // Older servers omit the field. Only those need the compatibility lookup;
    // null from the current server is an authoritative empty result.
    if (!enabled || !scope.length || hasInlineSuggestions)
      return () => {
        generation.current++;
      };
    setBusy('Loading suggestions…');
    fetchers
      .getSessionProjectSuggestions(sessionId)
      .then(items => {
        if (version === generation.current)
          setSnapshot({
            scopeKey,
            suggestions: Object.fromEntries(
              items
                .filter(item => scope.some(([id]) => id === item.segment_id))
                .map(item => [item.segment_id, item.suggestion])
            ),
          });
      })
      .catch(e => {
        if (version === generation.current) setError(assignErrorMessage(e));
      })
      .finally(() => {
        if (version === generation.current) setBusy(null);
      });
    return () => {
      generation.current++;
    };
  }, [enabled, sessionId, scopeKey, fetchers, hasInlineSuggestions, initialSuggestions]);

  const predict = async (eligible: AgentPeriod[]) => {
    if (busy) return;
    const version = ++generation.current;
    const current = () => version === generation.current;
    clearError();
    clearTimers();
    setArrived(false);
    // Each request only resolves once its prediction is done, so there is no partial
    // progress to report; the wording only reflects how long it has taken.
    setBusy('Finding projects…');
    setProgress('finding');
    timers.current.push(
      setTimeout(() => current() && setProgress('still'), STILL_CHECKING_AFTER_MS),
      setTimeout(() => current() && setProgress('slow'), SLOW_CHECK_AFTER_MS)
    );
    const queue = [...eligible];
    const retried = new Set<number>();
    const unfinished: number[] = [];
    let failure: unknown = null;
    let found = false;
    const run = async () => {
      while (!failure && current() && queue.length) {
        const period = queue.shift()!;
        try {
          const result = await fetchers.generateProjectSuggestion(
            sessionId,
            period.segmentId,
            wait => {
              if (current()) setNotice(wait === 'offline' ? OFFLINE_NOTICE : null);
            }
          );
          if (!current()) return;
          if (result && hasDisplayableProject(result)) found = true;
          setSuggestions(previous => {
            const next = { ...previous };
            if (result) next[period.segmentId] = result;
            else delete next[period.segmentId];
            return next;
          });
        } catch (e) {
          // The period changed while it was checked: one more check uses its latest activity.
          if (
            e instanceof PredictionUnfinished &&
            e.reason === 'stale' &&
            !retried.has(period.segmentId)
          ) {
            retried.add(period.segmentId);
            queue.push(period);
            continue;
          }
          // Keep in-flight successes, but stop launching work after a failure.
          failure = e;
          unfinished.push(period.segmentId);
          if (current()) setError(assignErrorMessage(e));
        }
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(4, eligible.length) }, run));
    } finally {
      if (current()) {
        clearTimers();
        setBusy(null);
        setNotice(null);
        setProgress(null);
        const final =
          failure instanceof PredictionUnfinished && FINAL_REASONS.includes(failure.reason);
        if (failure && !final) setRetryIds([...unfinished, ...queue.map(p => p.segmentId)]);
        if (found) {
          setArrived(true);
          // Always clears: a row refresh in the meantime must not leave the highlight on.
          timers.current.push(setTimeout(() => setArrived(false), ARRIVED_HIGHLIGHT_MS));
        }
      }
    }
  };
  const generate = (ids?: number[]) =>
    predict(
      periods
        .filter(
          p =>
            (p.projectId === null || ids !== undefined) &&
            !(p.projectAssignmentSource === 'inherited' && !p.projectPredictionState) &&
            !['waiting_summary', 'queued', 'running'].includes(p.projectPredictionState ?? '') &&
            p.summary?.trim() &&
            (!ids || ids.includes(p.segmentId))
        )
        .sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt))
    );
  // These periods' checks already ended here, whatever state the row last showed.
  const retry = retryIds
    ? () => void predict(periods.filter(p => retryIds.includes(p.segmentId)))
    : null;
  const accept = async (ids: number[], projectId?: number, fillIds: number[] = []) => {
    if (busy || !changeSession) return false;
    const version = ++generation.current;
    setBusy('Accepting…');
    clearError();
    try {
      const items = ids.map(id => ({
        segment_id: id,
        fingerprint: suggestions[id].fingerprint,
        ...(projectId === undefined ? {} : { project_id: projectId }),
      }));
      const acceptedProjectIds = uniq(
        ids.flatMap(id => {
          const chosen = projectId ?? suggestions[id].project_id;
          return chosen === null ? [] : [chosen];
        })
      );
      let accepted = false;
      await changeSession(async () => {
        const result = await fetchers.acceptSessionProjectSuggestions(
          sessionId,
          items,
          ...((fillIds.length ? [fillIds] : []) as [number[]?])
        );
        // Settled before the accepted session is applied, which can re-scope this provider.
        if (version === generation.current) {
          accepted = true;
          setUndoToken(result.undoToken);
          setSuggestions(previous =>
            Object.fromEntries(Object.entries(previous).filter(([id]) => !ids.includes(Number(id))))
          );
        }
        return result.session;
      });
      // Used once the server confirmed, even if this provider was re-scoped meanwhile.
      onAccepted?.(acceptedProjectIds);
      return accepted;
    } catch (e) {
      if (version === generation.current) setError(assignErrorMessage(e));
      return false;
    } finally {
      if (version === generation.current) setBusy(null);
    }
  };
  const undo = async () => {
    if (busy || !undoToken || !changeSession) return;
    const version = ++generation.current;
    setBusy('Undoing…');
    clearError();
    try {
      await changeSession(async () => {
        const session = await fetchers.undoSessionProjectSuggestions(sessionId, undoToken);
        if (version === generation.current) setUndoToken(null);
        return session;
      });
    } catch (e) {
      if (version === generation.current) setError(assignErrorMessage(e));
    } finally {
      if (version === generation.current) setBusy(null);
    }
  };
  return (
    <Context.Provider
      value={
        enabled
          ? {
              suggestions,
              busy,
              error,
              notice,
              progress,
              retry,
              arrived,
              generate,
              accept,
              undo,
              canUndo: !!undoToken,
            }
          : null
      }
    >
      {children}
    </Context.Provider>
  );
}
